// ---------------------------------------------------------------------------
// RichEditor — one WYSIWYG contentEditable editor shared by descriptions AND
// comments (Pat/Destiny asked for bold, hyperlinks, and checkboxes in both).
// Toolbar: B / I / U / S / text color / bullet / numbered / link / checklist /
// table. Optional @mentions. Text color (Allyson, 9/09) is a CLASS per color,
// applied through foreColor + a sentinel swap — see richColor.ts for why. Tables (Spencer, 7/27): insert a 3×3 grid, Tab between cells,
// right-click for row/column surgery, and Google-Docs table pastes keep their
// structure (richTables.ts). Exposes an imperative handle (getHtml /
// getMentionUids / clear / focus).
// ---------------------------------------------------------------------------

import { forwardRef, useImperativeHandle, useRef, useState, type ReactNode } from 'react';
import { ArrowDownToLine, ArrowLeftToLine, ArrowRightToLine, ArrowUpToLine, Link, List, ListOrdered, SquareCheck, Table, Trash2 } from 'lucide-react';
import { Avatar, Menu, MenuItem } from '@/core/ui/atoms';
import { Icon } from '@/core/ui/icons';
import {
  addColumn, addRow, deleteColumn, deleteRow, deleteTable, findCell,
  insertLineBreakInCell, nextCellOnTab, placeCaret, sanitizePastedHtml, tableTemplateHtml,
} from '@/core/ui/richTables';
import { INK_COLORS, adoptInkColor, inkSentinel } from '@/core/ui/richColor';

export interface RichEditorHandle {
  getHtml: () => string;
  getMentionUids: () => number[];
  isEmpty: () => boolean;
  clear: () => void;
  focus: () => void;
}

interface MentionUser { id: number; name: string; avatarUrl: string | null }

interface Props {
  initialHtml?: string;
  placeholder?: string;
  minHeight?: string;
  /** Cap the editable's growth; past it the TEXT scrolls inside the editor.
      Needed wherever the editor lives in a fixed-height flex column (the xl
      task-page activity rail) — an uncapped editable there grows until it
      pushes its own Send button out of the clipped column. */
  maxHeight?: string;
  autoFocus?: boolean;
  mentionUsers?: MentionUser[];
  onInput?: () => void;               // fires on every edit (draft caching)
  onSubmitShortcut?: () => void;      // ⌘/Ctrl+Enter
  onPasteFiles?: (files: File[]) => boolean; // return true if handled (images → attach)
}

const TOOLBAR: Array<[cmd: string, label: ReactNode, title: string, cls?: string]> = [
  ['bold', 'B', 'Bold (⌘/Ctrl+B)', 'font-bold'],
  ['italic', 'I', 'Italic', 'italic'],
  ['underline', 'U', 'Underline', 'underline'],
  // Asked for by Accounting (crossing off paid bonus lines): both sanitizers
  // always KEPT <s> — only the button was missing.
  ['strikeThrough', 'S', 'Strikethrough', 'line-through'],
  ['insertUnorderedList', <Icon i={List} size={14} />, 'Bullet list', ''],
  ['insertOrderedList', <Icon i={ListOrdered} size={14} />, 'Numbered list', ''],
];

const CHECK_BOX = '<span class="cg-check-box" contenteditable="false"></span>';
// Checked-state rides on a CLASS (not a data-* attr): Odoo's mail sanitizer
// strips data-* from comment bodies, so data-checked wouldn't persist. `class`
// survives both descriptions and comments.
const DONE_CLASS = 'cg-done';

export const RichEditor = forwardRef<RichEditorHandle, Props>(function RichEditor(
  { initialHtml = '', placeholder = 'Write…', minHeight = '90px', maxHeight, autoFocus, mentionUsers, onInput, onSubmitShortcut, onPasteFiles },
  ref,
) {
  const elRef = useRef<HTMLDivElement | null>(null);
  const [picker, setPicker] = useState<{ query: string; x: number; y: number; range: Range } | null>(null);
  const [pick, setPick] = useState(0);
  // Right-click table menu — viewport coords (the Menu atom is position:fixed).
  const [tableMenu, setTableMenu] = useState<{ top: number; left: number; cell: HTMLTableCellElement } | null>(null);

  // Match on any word start, not just the whole-name prefix, so "destiny",
  // "rodriguez", or "des" all reach Destiny Rodriguez — then rank prefix >
  // word-start > substring so the person you typed lands at the top. Cap raised
  // from 6 to 50 with a scrollable list (52-person roster): the old slice(0,6)
  // is exactly why "can't scroll down / type the whole name to add Destiny".
  const candidates = (() => {
    if (!picker) return [] as MentionUser[];
    const q = picker.query;
    if (!q) return (mentionUsers ?? []).slice(0, 50);
    const rank = (name: string): number => {
      const n = name.toLowerCase();
      if (n.startsWith(q)) return 0;
      if (n.split(/\s+/).some((w) => w.startsWith(q))) return 1;
      return n.includes(q) ? 2 : -1;
    };
    return (mentionUsers ?? [])
      .map((u) => ({ u, r: rank(u.name) }))
      .filter((x) => x.r >= 0)
      .sort((a, b) => a.r - b.r || a.u.name.localeCompare(b.u.name))
      .slice(0, 50)
      .map((x) => x.u);
  })();

  useImperativeHandle(ref, () => ({
    getHtml: () => {
      const el = elRef.current;
      if (!el) return '';
      // Serialize a clone so contenteditable="false" attrs on chips don't leak.
      const clone = el.cloneNode(true) as HTMLElement;
      clone.querySelectorAll('[contenteditable]').forEach((n) => n.removeAttribute('contenteditable'));
      // Our own text colors first: any sentinel still inline (typed after a
      // caret-only pick) becomes its class before the foreign scrub below
      // would eat it.
      adoptInkColor(clone);
      // Foreign inline styles: rich pastes (Google Docs / Word) bake their own ink
      // into style="" — black text on our dark canvas (Spencer's event notes,
      // 7/27). The app never writes inline color/font styles (ours are classes),
      // so these properties are always foreign; scrubbing at serialize time
      // catches every path into the editor and progressively cleans legacy
      // content whenever it's edited.
      clone.querySelectorAll<HTMLElement>('[style]').forEach((n) => {
        n.style.removeProperty('color');
        n.style.removeProperty('background-color');
        n.style.removeProperty('font-family');
        n.style.removeProperty('font-size');
        if (!n.getAttribute('style')?.trim()) n.removeAttribute('style');
      });
      return clone.innerHTML;
    },
    getMentionUids: () => {
      const el = elRef.current;
      if (!el) return [];
      return Array.from(el.querySelectorAll('[data-uid]'))
        .map((n) => Number((n as HTMLElement).dataset.uid))
        .filter((n) => Number.isInteger(n));
    },
    isEmpty: () => {
      const el = elRef.current;
      if (!el) return true;
      return !el.textContent?.trim() && !el.querySelector('img, table, .cg-check-box, [data-uid]');
    },
    clear: () => { if (elRef.current) elRef.current.innerHTML = ''; },
    focus: () => elRef.current?.focus(),
  }), []);

  const cmd = (c: string) => { elRef.current?.focus(); document.execCommand(c); onInput?.(); };

  // ---- text color ----
  // The palette is a Menu (position:fixed — the editor's outer wrapper clips
  // overflow, so an absolute popover would be cut off). Opening it must not
  // lose the selection the color is for: the trigger's mousedown is
  // preventDefault'd and the live range is cloned, then restored right before
  // the command runs.
  const [inkMenu, setInkMenu] = useState<{ top: number; left: number; range: Range | null } | null>(null);
  const openInkMenu = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    const el = elRef.current;
    const sel = window.getSelection();
    let range: Range | null = null;
    if (el && sel && sel.rangeCount && el.contains(sel.getRangeAt(0).commonAncestorContainer)) range = sel.getRangeAt(0).cloneRange();
    const r = e.currentTarget.getBoundingClientRect();
    setInkMenu({ top: r.bottom + 4, left: r.left, range });
  };
  const applyInk = (key: string | null) => {
    const menu = inkMenu;
    setInkMenu(null);
    const el = elRef.current;
    if (!el || !menu) return;
    el.focus();
    const sel = window.getSelection();
    if (menu.range && sel) { sel.removeAllRanges(); sel.addRange(menu.range); }
    // A bare caret means "this word" — nobody selects a word to recolor it
    // one letter at a time. If the caret sits in whitespace nothing moves and
    // we do nothing rather than color the next thing they type near-black.
    if (sel && sel.isCollapsed) {
      sel.modify('move', 'backward', 'word');
      sel.modify('extend', 'forward', 'word');
      if (sel.isCollapsed) return;
      // Windows word-extend swallows the trailing space; give it back so the
      // color stops at the letters (proved in the 9/09 click-through).
      const r = sel.getRangeAt(0);
      if (r.endContainer.nodeType === Node.TEXT_NODE) {
        const text = r.endContainer.textContent ?? '';
        let end = r.endOffset;
        while (end > 0 && /\s/.test(text[end - 1] ?? '') && !(r.endContainer === r.startContainer && end <= r.startOffset)) end--;
        if (end !== r.endOffset) r.setEnd(r.endContainer, end);
      }
      if (r.collapsed) return;
    }
    // styleWithCSS so the engine writes style="color" (a span) instead of a
    // <font color> — adoptInkColor reads both, but one shape keeps the DOM
    // predictable. Restored after, so bold/italic keep producing <b>/<i>.
    document.execCommand('styleWithCSS', false, 'true');
    document.execCommand('foreColor', false, inkSentinel(key));
    document.execCommand('styleWithCSS', false, 'false');
    adoptInkColor(el);
    onInput?.();
  };

  const insertLink = () => {
    const el = elRef.current;
    if (!el) return;
    el.focus();
    const url = window.prompt('Link URL (https://…):', 'https://');
    if (!url || url === 'https://') return;
    const safe = /^(https?:|mailto:)/i.test(url) ? url : `https://${url}`;
    const sel = window.getSelection();
    if (sel && sel.rangeCount && !sel.isCollapsed) {
      document.execCommand('createLink', false, safe);
    } else {
      document.execCommand('insertHTML', false, `<a href="${safe.replace(/"/g, '&quot;')}">${safe.replace(/[<>]/g, '')}</a>&nbsp;`);
    }
    onInput?.();
  };

  const insertChecklist = () => {
    const el = elRef.current;
    if (!el) return;
    el.focus();
    document.execCommand('insertHTML', false,
      `<ul class="cg-checklist"><li class="cg-check">${CHECK_BOX}&nbsp;</li></ul><p><br></p>`);
    onInput?.();
  };

  const insertTable = () => {
    const el = elRef.current;
    if (!el) return;
    el.focus();
    document.execCommand('insertHTML', false, tableTemplateHtml());
    // execCommand hands back no node — find the fresh table by its ephemeral
    // marker, strip the marker, and land the caret in the first cell.
    const t = el.querySelector('table[data-cgt-new]');
    if (t) {
      t.removeAttribute('data-cgt-new');
      const first = t.querySelector<HTMLTableCellElement>('th, td');
      if (first) placeCaret(first);
    }
    onInput?.();
  };

  /** Run one context-menu table op: close the menu, take focus back from the
   *  clicked MenuItem, mutate, and park the caret in the op's follow-up cell. */
  const tableAction = (act: (cell: HTMLTableCellElement) => HTMLTableCellElement | null | void) => {
    const menu = tableMenu;
    setTableMenu(null);
    if (!menu || !menu.cell.isConnected) return;
    elRef.current?.focus();
    const target = act(menu.cell);
    if (target) placeCaret(target);
    onInput?.();
  };

  // Click a checkbox while editing → toggle it (don't move the caret into it).
  const onClick = (e: React.MouseEvent) => {
    const t = e.target as HTMLElement;
    if (t.classList?.contains('cg-check-box')) {
      e.preventDefault();
      const li = t.closest('li');
      if (li) {
        const done = li.classList.contains(DONE_CLASS) || li.getAttribute('data-checked') === 'true';
        li.removeAttribute('data-checked'); // clear any legacy encoding
        li.classList.toggle(DONE_CLASS, !done);
        onInput?.();
      }
    }
  };

  // ---- mentions ----
  const scanMention = () => {
    if (!mentionUsers?.length) return;
    const sel = window.getSelection();
    if (!sel || !sel.rangeCount || !sel.isCollapsed) { setPicker(null); return; }
    const node = sel.anchorNode;
    if (!node || node.nodeType !== Node.TEXT_NODE) { setPicker(null); return; }
    const text = (node.textContent ?? '').slice(0, sel.anchorOffset);
    const at = text.lastIndexOf('@');
    if (at < 0 || (at > 0 && !/[\s([{]/.test(text[at - 1]))) { setPicker(null); return; }
    const query = text.slice(at + 1);
    if (/[\s@]/.test(query) || query.length > 30) { setPicker(null); return; }
    const r = document.createRange();
    r.setStart(node, at);
    r.setEnd(node, sel.anchorOffset);
    const rect = r.getBoundingClientRect();
    const host = elRef.current!.getBoundingClientRect();
    const nextQuery = query.toLowerCase();
    // Reset the highlight to the top ONLY when the query actually changed. This
    // handler also fires on keyUP of the Arrow keys (whose keyDown moved `pick`),
    // and an unconditional setPick(0) here snapped the selection back to the first
    // row on every arrow press — so keyboard nav could never leave the top match.
    setPick((prev) => (picker && picker.query === nextQuery ? prev : 0));
    setPicker({ query: nextQuery, x: rect.left - host.left, y: rect.bottom - host.top, range: r.cloneRange() });
  };

  const choose = (u: MentionUser) => {
    if (!picker) return;
    picker.range.deleteContents();
    const chip = document.createElement('span');
    chip.className = 'cg-mention';
    chip.setAttribute('data-uid', String(u.id));
    chip.setAttribute('contenteditable', 'false');
    chip.textContent = `@${u.name}`;
    picker.range.insertNode(chip);
    // caret + a trailing space after the chip
    const space = document.createTextNode(' ');
    chip.after(space);
    const sel = window.getSelection();
    const r = document.createRange();
    r.setStartAfter(space);
    r.collapse(true);
    sel?.removeAllRanges();
    sel?.addRange(r);
    setPicker(null);
    elRef.current?.focus();
    onInput?.();
  };

  return (
    <div className="overflow-hidden rounded-lg border border-line focus-within:border-brand">
      <div className="flex flex-wrap items-center gap-0.5 border-b border-line bg-page px-1.5 py-1">
        {TOOLBAR.map(([c, l, title, cls]) => (
          <button key={c} type="button" title={title} onMouseDown={(e) => { e.preventDefault(); cmd(c); }}
            className={`rounded px-1.5 py-0.5 text-xs text-muted hover:bg-hover hover:text-ink ${cls}`}>{l}</button>
        ))}
        <button type="button" title="Text color" onMouseDown={openInkMenu}
          className="cg-ink-trigger rounded px-1.5 py-0.5 text-xs text-muted hover:bg-hover hover:text-ink">A</button>
        <button type="button" title="Insert link" onMouseDown={(e) => { e.preventDefault(); insertLink(); }}
          className="rounded px-1.5 py-0.5 text-xs text-muted hover:bg-hover hover:text-ink"><Icon i={Link} size={14} /></button>
        <button type="button" title="Checklist" onMouseDown={(e) => { e.preventDefault(); insertChecklist(); }}
          className="rounded px-1.5 py-0.5 text-xs text-muted hover:bg-hover hover:text-ink"><Icon i={SquareCheck} size={14} /></button>
        <button type="button" title="Insert table" onMouseDown={(e) => { e.preventDefault(); insertTable(); }}
          className="rounded px-1.5 py-0.5 text-xs text-muted hover:bg-hover hover:text-ink"><Icon i={Table} size={14} /></button>
      </div>
      <div className="relative">
        <div
          ref={(el) => {
            elRef.current = el;
            if (el && !el.dataset.seeded) {
              el.innerHTML = initialHtml;
              // Re-atomicize chips: Odoo strips contenteditable="false" on write,
              // so a re-seeded mention/checkbox would be freely editable (and
              // corruptible). Restore the guard on mount.
              el.querySelectorAll('.cg-mention, .cg-check-box').forEach((n) => n.setAttribute('contenteditable', 'false'));
              el.dataset.seeded = '1';
              if (autoFocus) el.focus();
            }
          }}
          contentEditable
          suppressContentEditableWarning
          data-placeholder={placeholder}
          onInput={() => { onInput?.(); scanMention(); }}
          onKeyUp={scanMention}
          onClick={onClick}
          onPaste={(e) => {
            const files = Array.from(e.clipboardData?.files ?? []);
            if (files.length && onPasteFiles?.(files)) { e.preventDefault(); return; }
            // Table paste-through: when the clipboard's HTML flavor carries a
            // real <table>, sanitize it down to structure (richTables allow-list;
            // attrs stripped to a.href + colspan/rowspan) and insert the RICH
            // version — Spencer's Google Docs event tables used to flatten here.
            const rich = e.clipboardData?.getData('text/html');
            if (rich && /<table[\s>]/i.test(rich)) {
              const safe = sanitizePastedHtml(rich);
              if (safe) { e.preventDefault(); document.execCommand('insertHTML', false, safe); onInput?.(); return; }
            }
            // Every other paste keeps the plain-text discipline (no Word/HTML cruft).
            const text = e.clipboardData?.getData('text/plain');
            if (text) { e.preventDefault(); document.execCommand('insertText', false, text); onInput?.(); }
          }}
          onContextMenu={(e) => {
            // Right-click inside a table cell = the table menu; everywhere else
            // keeps the browser's own menu (spellcheck, paste, …).
            const cell = findCell(e.target as Node, elRef.current);
            if (!cell) return;
            e.preventDefault();
            setTableMenu({ top: e.clientY, left: e.clientX, cell });
          }}
          onKeyDown={(e) => {
            if (picker && candidates.length) {
              if (e.key === 'ArrowDown') { e.preventDefault(); setPick((p) => Math.min(p + 1, candidates.length - 1)); return; }
              if (e.key === 'ArrowUp') { e.preventDefault(); setPick((p) => Math.max(p - 1, 0)); return; }
              if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); const c = candidates[pick] ?? candidates[0]; if (c) choose(c); return; }
              if (e.key === 'Escape') { setPicker(null); return; }
            }
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); onSubmitShortcut?.(); return; }
            // Table keys — ONLY when the caret sits in a cell of ours; outside a
            // table, Tab and Enter keep their stock behavior untouched.
            if (e.key === 'Tab' || e.key === 'Enter') {
              const cell = findCell(window.getSelection()?.anchorNode ?? null, elRef.current);
              if (!cell) return;
              if (e.key === 'Tab') {
                e.preventDefault(); // Tab never leaves the editor mid-table
                const { cell: target, grewRow } = nextCellOnTab(cell, e.shiftKey);
                if (target) { placeCaret(target); if (grewRow) onInput?.(); }
                return;
              }
              // Plain Enter stays INSIDE the cell (no table-splitting); Shift+Enter
              // already inserts a <br> natively and passes through.
              if (!e.shiftKey) { e.preventDefault(); insertLineBreakInCell(cell); onInput?.(); }
            }
          }}
          className="cg-prose cg-editor px-3 py-2 text-[14px] outline-none"
          style={{ minHeight, ...(maxHeight ? { maxHeight, overflowY: 'auto' as const } : {}) }}
        />
        {picker && candidates.length > 0 && (
          <div className="absolute z-20 max-h-56 w-64 overflow-y-auto overscroll-contain rounded-md border border-line bg-canvas shadow-xl"
            style={{ left: Math.max(0, picker.x), top: picker.y + 4 }}>
            {candidates.map((u, i) => (
              <button key={u.id} type="button"
                ref={(el) => { if (i === pick && el) el.scrollIntoView({ block: 'nearest' }); }}
                onMouseDown={(e) => { e.preventDefault(); choose(u); }}
                onMouseEnter={() => setPick(i)}
                className={`flex w-full items-center gap-2 px-2 py-1.5 text-left text-[13px] ${i === pick ? 'bg-hover' : ''}`}>
                <Avatar person={u} size={20} />
                <span className="truncate text-ink">{u.name}</span>
              </button>
            ))}
          </div>
        )}
        {inkMenu && (
          <Menu anchor={{ top: inkMenu.top, left: inkMenu.left }} width={236} onClose={() => setInkMenu(null)}>
            <div className="flex items-center gap-1 px-2 py-1">
              <button type="button" title="Default" aria-label="Default text color"
                onMouseDown={(e) => { e.preventDefault(); applyInk(null); }}
                className="cg-ink-swatch cg-ink-swatch-none">A</button>
              {INK_COLORS.map((c) => (
                <button key={c.key} type="button" title={c.label} aria-label={`${c.label} text`}
                  onMouseDown={(e) => { e.preventDefault(); applyInk(c.key); }}
                  className={`cg-ink-swatch ${c.cls}`} />
              ))}
            </div>
          </Menu>
        )}
        {tableMenu && (
          <Menu anchor={{ top: tableMenu.top, left: tableMenu.left }} width={192} onClose={() => setTableMenu(null)}>
            <MenuItem icon={<Icon i={ArrowUpToLine} size={14} />} label="Add row above" onSelect={() => tableAction((c) => addRow(c, 'above'))} />
            <MenuItem icon={<Icon i={ArrowDownToLine} size={14} />} label="Add row below" onSelect={() => tableAction((c) => addRow(c, 'below'))} />
            <MenuItem icon={<Icon i={ArrowLeftToLine} size={14} />} label="Add column left" onSelect={() => tableAction((c) => addColumn(c, 'left'))} />
            <MenuItem icon={<Icon i={ArrowRightToLine} size={14} />} label="Add column right" onSelect={() => tableAction((c) => addColumn(c, 'right'))} />
            <MenuItem icon={<Icon i={Trash2} size={14} />} label="Delete row" danger onSelect={() => tableAction(deleteRow)} />
            <MenuItem icon={<Icon i={Trash2} size={14} />} label="Delete column" danger onSelect={() => tableAction(deleteColumn)} />
            <MenuItem icon={<Icon i={Trash2} size={14} />} label="Delete table" danger onSelect={() => tableAction(deleteTable)} />
          </Menu>
        )}
      </div>
    </div>
  );
});
